import { Card, Text, H2 } from '@payaca/ambrosia';
import { FC, useCallback, useState, type JSX } from 'react';
import { solarSavingsRegion } from '@payaca/helpers/solarSavingsConstants';
import { useRegion } from '@/api/rest/users/useRegion';
import useUpdateAccountSolarSettings from '@/api/mutations/account/useUpdateAccountSolarSettings';
import useGetMyAccountSolarSettings from '@/api/queries/me/useGetMyAccountSolarSettings';
import { type AccountSolarSettingsFieldsFragment } from '@/gql/graphql';
import PlacementRulesFields, {
  type EditablePlacementRules,
} from './PlacementRulesFields';
import SettingsCardPlaceholder from './SettingsCardPlaceholder';
import SettingsSaveButton from './SettingsSaveButton';
import {
  changedPlacementRules,
  placementRulesFromGql,
} from './solarPlacementRulesInput';
import { useBusinessAccountForm } from './useBusinessAccountForm';

type FormState = { solarPlacementRules: EditablePlacementRules };

type Props = { readOnly: boolean };

const CompanySettingsSolarPlacement: FC<Props> = ({
  readOnly,
}: Props): JSX.Element => {
  const { solarSettings, isError } = useGetMyAccountSolarSettings();

  if (!solarSettings) {
    return <SettingsCardPlaceholder isError={isError} />;
  }

  return (
    <SolarPlacementForm
      key={JSON.stringify(solarSettings.placementRules)}
      readOnly={readOnly}
      settings={solarSettings}
    />
  );
};

const SolarPlacementForm: FC<
  Props & { settings: AccountSolarSettingsFieldsFragment }
> = ({ readOnly, settings }) => {
  const region = useRegion();
  const { mutateAsync: updateAccountSolarSettings } =
    useUpdateAccountSolarSettings();
  // Diff against what the form loaded or last saved, not the live query: a
  // refetch must not turn another writer's change into a field to revert.
  const [baseline, setBaseline] = useState(() =>
    placementRulesFromGql(settings.placementRules)
  );
  const save = useCallback(
    async ({ solarPlacementRules }: FormState) => {
      await updateAccountSolarSettings({
        placementRules: changedPlacementRules(baseline, solarPlacementRules),
      });
      setBaseline(solarPlacementRules);
    },
    [updateAccountSolarSettings, baseline]
  );
  const {
    formState,
    updateFormFields,
    saveAndFlush,
    isValid,
    isDirty,
    isSaving,
  } = useBusinessAccountForm<FormState>(
    { solarPlacementRules: baseline },
    {},
    save
  );

  return (
    <Card sizeVariant="lg">
      <Card.Body className="flex flex-col gap-4">
        <div>
          <H2>How we lay arrays</H2>
          <Text>
            Fire-break, panel gap and which roofs get modules. New designs use
            these. Open Array rules on a design to change that job only.
          </Text>
        </div>
        <PlacementRulesFields
          value={formState.solarPlacementRules}
          disabled={readOnly}
          region={solarSavingsRegion(region)}
          onChange={(solarPlacementRules) =>
            updateFormFields({ solarPlacementRules })
          }
        />
        {!readOnly && (
          <SettingsSaveButton
            label="Save array rules"
            isDirty={isDirty}
            isSaving={isSaving}
            isValid={isValid}
            onSave={saveAndFlush}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default CompanySettingsSolarPlacement;
